import React, { Fragment } from 'react';
import { Matrix4 } from 'three';

import { PlateTypeProperties } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { decomposeShapeKey } from '../../util/PlateGeometryCache';
import { useHiddenEnvelopeGuids, useSimplifiedEnvelopeGuids } from './EnvelopeLodMonitor';
import { InstancedPlateMesh } from './InstancedPlateMesh';

export type PlateForBulkRendering = {
  readonly trussEnvelopeGuid: string;
  readonly plateShapeKey: string;
  readonly matrix: Matrix4;
};

/**
 * Renders instanced meshes for all the given plates.
 */
export function BulkPlatePairsController(props: {
  data: PlateForBulkRendering[];
  plateTypePropertiesDictionary: { [getPlateTypeStringResult: string]: PlateTypeProperties | undefined };
}) {
  const hiddenEnvelopeGuids = useHiddenEnvelopeGuids();
  const simplifiedEnvelopeGuids = useSimplifiedEnvelopeGuids();

  if (hiddenEnvelopeGuids === 'invalid') return;

  const plateShapeKeys = new Set(props.data.map((plate) => plate.plateShapeKey));

  const visiblePlates = props.data.filter((plate) => !hiddenEnvelopeGuids.has(plate.trussEnvelopeGuid));
  const [simplifiedPlates, normalPlates] = partitionPlates(plateShapeKeys, visiblePlates, (plate) =>
    simplifiedEnvelopeGuids.has(plate.trussEnvelopeGuid),
  );

  return Array.from(plateShapeKeys)
    .map((plateShapeKey) => {
      const { getPlateTypeStringResult, plateType, length, width, geometryType, hingeAngle } =
        decomposeShapeKey(plateShapeKey);
      const properties = props.plateTypePropertiesDictionary[getPlateTypeStringResult];
      if (properties == null) return null;

      const proportions = {
        length,
        width,
        plateType,
        geometryType,
        thickness: properties.thickness,
        hingeAngle,
      };

      return (
        <Fragment key={plateShapeKey}>
          <InstancedPlateMesh {...proportions} matrices={normalPlates.findByShapeKey(plateShapeKey)} />
          <InstancedPlateMesh simplified {...proportions} matrices={simplifiedPlates.findByShapeKey(plateShapeKey)} />
        </Fragment>
      );
    })
    .filter(notNullish);
}

/**
 * Partitions the given plates into two `PlateMatrixCollection` objects based on the given predicate function.
 */
function partitionPlates(
  plateShapeKeys: Set<string>,
  sources: PlateForBulkRendering[],
  predicate: (source: PlateForBulkRendering) => boolean,
): [PlateMatrixCollection, PlateMatrixCollection] {
  const collectionA = new PlateMatrixCollection(plateShapeKeys);
  const collectionB = new PlateMatrixCollection(plateShapeKeys);

  for (const source of sources) {
    if (predicate(source)) {
      collectionA.add(source.plateShapeKey, source.matrix);
    } else {
      collectionB.add(source.plateShapeKey, source.matrix);
    }
  }

  return [collectionA, collectionB];
}

/**
 * Utility class for grouping plate matrices by their plate shape key. Must be initialized with a set of all supported
 * plate shape keys for performance.
 */
class PlateMatrixCollection {
  plates = new Map<string, Matrix4[]>();

  constructor(validPlateShapeKeys: Set<string>) {
    for (const plateShapeKey of validPlateShapeKeys) {
      this.plates.set(plateShapeKey, []);
    }
  }

  add(plateShapeKey: string, matrix: Matrix4) {
    this.plates.get(plateShapeKey)!.push(matrix);
  }

  findByShapeKey(plateShapeKey: string) {
    return this.plates.get(plateShapeKey)!;
  }
}
